/**
 * SubTeamsPanel — "team within a team" (Jun 2026).
 * Lets admins and the team's supervisors carve smaller squads out of a
 * team for specific tasks. Sub-team members stay on the parent team;
 * sub-teams are assignable to jobs everywhere teams are.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { Users, Plus, Trash2, Pencil, Loader2, ChevronDown, ChevronRight, Crown } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export const SubTeamsPanel = ({ team, token }) => {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [subteams, setSubteams] = useState([]);
  const [parentMembers, setParentMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', member_ids: [], leader_id: null });

  const hdr = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [subs, members] = await Promise.all([
        axios.get(`${API}/teams/${team.id}/subteams`, hdr),
        axios.get(`${API}/teams/${team.id}/members`, hdr),
      ]);
      setSubteams(subs.data.subteams || []);
      setParentMembers(members.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Could not load sub-teams', variant: 'destructive' });
    }
    setLoading(false);
  };

  useEffect(() => { if (expanded) load(); /* eslint-disable-next-line */ }, [expanded, team.id]);

  const openCreate = () => { setEditingId(null); setForm({ name: '', member_ids: [], leader_id: null }); setShowForm(true); };
  const openEdit = (s) => { setEditingId(s.id); setForm({ name: s.name, member_ids: [...s.member_ids], leader_id: s.leader_id || null }); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim() || form.member_ids.length === 0) {
      toast({ title: 'Missing info', description: 'Name + at least one member required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await axios.put(`${API}/subteams/${editingId}`, form, hdr);
        toast({ title: 'Sub-team updated' });
      } else {
        await axios.post(`${API}/teams/${team.id}/subteams`, form, hdr);
        toast({ title: 'Sub-team created', description: `${form.name} — assign it to jobs like any team.` });
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    }
    setBusy(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sub-team "${s.name}"? Jobs already assigned keep their workers.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/subteams/${s.id}`, hdr);
      setSubteams((prev) => prev.filter((x) => x.id !== s.id));
      toast({ title: 'Sub-team deleted' });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Delete failed', variant: 'destructive' });
    }
    setBusy(false);
  };

  return (
    <div className="mt-4 border border-[#2D3748] rounded-lg" data-testid={`subteams-panel-${team.id}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-[#0F1923] rounded-lg transition-colors"
        data-testid={`subteams-toggle-${team.id}`}
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <Users className="w-4 h-4 text-violet-400" />
        <span className="font-semibold text-sm text-white">Sub-teams</span>
        <span className="text-xs text-slate-500">smaller squads within {team.name}, assignable to jobs</span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {loading ? (
            <div className="py-3 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          ) : (
            <>
              {subteams.length === 0 && !showForm && (
                <p className="text-sm text-slate-500">No sub-teams yet.</p>
              )}
              {subteams.map((s) => (
                <div key={s.id} className="flex items-start gap-3 p-2.5 bg-[#0F1923] rounded-lg border border-[#2D3748]" data-testid={`subteam-row-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-white">{s.name}</p>
                    {s.leader_name && (
                      <p className="text-xs text-amber-400 flex items-center gap-1" data-testid={`subteam-leader-${s.id}`}>
                        <Crown className="w-3 h-3" /> Squad leader: {s.leader_name}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 truncate">{s.member_names.join(', ') || 'No members'}</p>
                  </div>
                  <span className="text-xs text-slate-500 whitespace-nowrap">{s.member_ids.length} member{s.member_ids.length === 1 ? '' : 's'}</span>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="h-7 px-2 text-slate-300" data-testid={`subteam-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s)} disabled={busy} className="h-7 px-2 text-red-400" data-testid={`subteam-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}

              {showForm ? (
                <div className="p-3 bg-[#0F1923] rounded-lg border border-violet-500/30 space-y-2" data-testid="subteam-form">
                  <Input
                    placeholder="Sub-team name (e.g. HVAC Squad)"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="bg-[#1A2433] border-[#2D3748] text-white"
                    data-testid="subteam-name-input"
                  />
                  <p className="text-xs text-slate-400">Pick members from {team.name} — tap the crown to set the squad leader:</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {parentMembers.map((m) => (
                      <div key={m.id} className="flex items-center gap-2 text-sm text-slate-200 py-0.5" data-testid={`subteam-member-option-${m.id}`}>
                        <Checkbox
                          checked={form.member_ids.includes(m.id)}
                          onCheckedChange={(v) => setForm((f) => ({
                            ...f,
                            member_ids: v ? [...f.member_ids, m.id] : f.member_ids.filter((x) => x !== m.id),
                            leader_id: !v && f.leader_id === m.id ? null : f.leader_id,
                          }))}
                        />
                        <span className="flex-1 cursor-default">{m.name}</span>
                        {form.member_ids.includes(m.id) && (
                          <button
                            type="button"
                            title={form.leader_id === m.id ? 'Squad leader (tap to unset)' : 'Make squad leader'}
                            onClick={() => setForm((f) => ({ ...f, leader_id: f.leader_id === m.id ? null : m.id }))}
                            className={`p-1 rounded transition-colors ${form.leader_id === m.id ? 'text-amber-400 bg-amber-500/10' : 'text-slate-600 hover:text-amber-400'}`}
                            data-testid={`subteam-leader-toggle-${m.id}`}
                          >
                            <Crown className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    {parentMembers.length === 0 && <p className="text-xs text-slate-500">This team has no members yet.</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="subteam-save-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Save changes' : 'Create sub-team')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="border-[#2D3748] text-slate-300">Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={openCreate} className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10" data-testid={`subteam-create-btn-${team.id}`}>
                  <Plus className="w-4 h-4 mr-1" />New sub-team
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubTeamsPanel;
